import { CdkListbox, CdkOption } from '@angular/cdk/listbox'
import { CommonModule } from '@angular/common'
import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { CompaniesService } from '@core/api/companies/companies.service'
import { CurrencyMaskDirective } from '@core/directives/currency-mask/currency-mask.directive'
import { PalantirBannerComponent, PalantirButtonComponent, PalantirConfirmDialogService, PalantirDialogService, PalantirIconComponent, PalantirInputComponent, PalantirLayoutComponent, PalantirRadioComponent, PalantirSelectComponent, PalantirSelectOption, PalantirSelectResult, PalantirSelectSearchParams, PalantirSkeletonComponent, PalantirTextComponent, PalantirToastModule } from '@fieldcontrol/palantir'
import { Company, CompanyPagedResult, Warranty } from '@generated/sammathnaur.graphql'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { addDays, format } from 'date-fns'
import { NgScrollbarModule } from 'ngx-scrollbar'
import { PermissionsService } from 'projects/fce-sidebar/src/lib/services/permissions/permissions.service'
import { Subject, distinctUntilChanged, takeUntil } from 'rxjs'
import { PalantirAccordionComponent } from './components/accordion/accordion.component'
import { WarrantiesDialogComponent } from './components/warranty-template-dialog/warranties-dialog.component'
import { WarrantiesMapperService } from './services/warranties.mapper.service'
import { WarrantyForm } from './warranties-equipment-tab.types'
import { EquipmentsService } from '../../core/api/equipments/equipments.service'
import { WarrantiesService } from '../../core/api/warranties/warranties.service'
import { PipesModule } from '../../core/pipes/pipes.module'

@Component({
    selector: 'app-warranties-equipment-tab',
    templateUrl: './warranties-equipment-tab.component.html',
    styleUrls: ['./warranties-equipment-tab.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        CdkListbox,
        CdkOption,
        MatFormFieldModule,
        PalantirIconComponent,
        PalantirButtonComponent,
        PalantirTextComponent,
        PalantirInputComponent,
        PalantirLayoutComponent,
        PalantirBannerComponent,
        PalantirAccordionComponent,
        CurrencyMaskDirective,
        PalantirRadioComponent,
        PalantirToastModule,
        PalantirSkeletonComponent,
        PalantirSelectComponent,
        PipesModule,
        NgScrollbarModule,
        ReactiveFormsModule,
        TranslateModule
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ],
})
export class EquipmentWarrantiesTabComponent implements OnInit, OnChanges {


  @Input({ required: true })
  public readonly equipmentId!: string

  @Input()
  public readonly hasCreateWarrantyPermission: boolean = false

  @Input()
  public readonly hasEditWarrantyPermission: boolean = false

  @Output() public readonly toggle = new EventEmitter<boolean>()

  public isOpen = signal(false)
  public activeIndex = signal<number | null>(null)
  public editingIndex = signal<number | null>(null)

  public toggleEdit(index: number) {
    this.editingIndex.set(this.editingIndex() === index ? null : index)
  }

  public cdr = inject(ChangeDetectorRef)
  private palantirDialogService = inject(PalantirDialogService)
  private equipmentService = inject(EquipmentsService)
  private readonly warrantiesService = inject(WarrantiesService)
  private readonly warrantiesMapperService = inject(WarrantiesMapperService)
  private readonly palantirConfirmDialogService = inject(PalantirConfirmDialogService)
  private readonly translateService = inject(TranslateService)
  public readonly permissionsService = inject(PermissionsService)
  private readonly companiesService = inject(CompaniesService)

  public warranties = signal<Warranty[]>([])
  public isLoading = signal<boolean>(true)

  private destroy$ = new Subject<void>()

  private updateWarrantyEvent = new CustomEvent('updateWarranty')

  public forms: FormGroup[] = []

  public async ngOnInit(): Promise<void> {
    await this.getWarranties()
    this.forms = this.warranties().map(this.warrantiesMapperService.createForm)
    this.observeCoverageStart()
  }

  public async openDialog(): Promise<void> {
    const dialogRef = this.palantirDialogService.openDialog(WarrantiesDialogComponent, {
      size: 'medium',
      data: {
        equipmentId: this.equipmentId
      }
    })

    const result = await dialogRef.afterClosed<{ reload: boolean }>()

    if (result.reload) {
      this.isLoading.set(true)
      window.dispatchEvent(this.updateWarrantyEvent)
      await this.ngOnInit()
      window.dispatchEvent(new Event('equipment-warranties-updated'))
    }
  }

  private async getWarranties(): Promise<void> {
    const equipment = await this.equipmentService.searchEquipmentWithWarranties(this.equipmentId)
    const warranties = equipment.data?.equipment?.warranties as Warranty[]
    this.warranties.set(warranties)
    this.isLoading.set(false)
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['equipment']) {
      await this.getWarranties()
      this.forms = this.warranties().map(this.warrantiesMapperService.createForm)
    }
  }

  public async saveWarranty(form: FormGroup<WarrantyForm>): Promise<void> {
    this.isLoading.set(true)
    const input = this.warrantiesMapperService.mapperWarrantyFormToInput(form)
    await this.warrantiesService.update(input)
    this.editingIndex.set(null)
    await this.getWarranties()
    window.dispatchEvent(this.updateWarrantyEvent)
    this.isLoading.set(false)
    window.dispatchEvent(new Event('equipment-warranties-updated'))
  }

  private observeCoverageStart() {
    for (const [, form] of this.forms.entries()) {
      form.get('coverageDurationDays')?.valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe((value) => {
          const coverageStartedAt = form.get('coverageStartedAt')?.value
          const endDate = format(addDays(new Date(coverageStartedAt), value), 'yyyy-MM-dd')
          form.get('coverageEndedAt')?.setValue(endDate)
          const extendedCoverageDurationDays = form.get('extendedCoverageDurationDays')?.value
          const extendEndDate = format(addDays(new Date(coverageStartedAt), Number(extendedCoverageDurationDays) + Number(value)), 'yyyy-MM-dd')
          form.get('extendedCoverageEndedAt')?.setValue(extendEndDate)
        })

      form.get('extendedCoverageDurationDays')?.valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe((value) => {
          const coverageStartedAt = form.get('coverageStartedAt')?.value
          const coverageDurationDays = form.get('coverageDurationDays')?.value
          const extendEndDate = format(addDays(new Date(coverageStartedAt), Number(coverageDurationDays) + Number(value)), 'yyyy-MM-dd')
          form.get('extendedCoverageEndedAt')?.setValue(extendEndDate)
        })

      form.get('hasCoverageExtend')?.valueChanges.pipe(
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe((value) => {
        if (value) {
          this.toggleValidatorField(true, form.get('extendedCoverageDurationDays') as FormControl)
          this.toggleValidatorField(true, form.get('extendedValue') as FormControl)
        }

        if (!value) {
          form.get('extendedCoverageDurationDays')?.reset()
          form.get('extendedValue')?.reset()
          this.toggleValidatorField(false, form.get('extendedCoverageDurationDays') as FormControl)
          this.toggleValidatorField(false, form.get('extendedValue') as FormControl)
        }
      })
    }
  }

  public async removeWarranty(warranty: Warranty): Promise<void> {
    if (['active', 'extended'].includes(warranty.status as string)) {

      const dialogRef = this.palantirConfirmDialogService.openDialog({
        title: this.translateService.instant('TAB_WARRANTIES_EQUIPMENT.DIALOG_REMOVE.TITLE'),
        text: this.translateService.instant('TAB_WARRANTIES_EQUIPMENT.DIALOG_REMOVE.INFO'),
        cancelText: this.translateService.instant('TAB_WARRANTIES_EQUIPMENT.DIALOG_REMOVE.BUTTONS.CANCEL'),
        successText: this.translateService.instant('TAB_WARRANTIES_EQUIPMENT.DIALOG_REMOVE.BUTTONS.REMOVE'),
      })

      const result = await dialogRef.afterClosed<{ role: string }>()

      if (result?.role !== 'success') {
        return
      }
    }

    this.isLoading.set(true)
    await this.warrantiesService.remove({ warrantyId: warranty.id })
    await this.getWarranties()
    window.dispatchEvent(new Event('equipment-warranties-updated'))
    window.dispatchEvent(this.updateWarrantyEvent)
  }

  public async cancelEdit(): Promise<void> {
    this.editingIndex.set(null)
    await this.ngOnInit()
  }

  private toggleValidatorField(isRequired: boolean, control: FormControl): void {
    if (isRequired) {
      control.setValidators([Validators.required, Validators.min(1), Validators.max(2147483647)])
    } else {
      control.removeValidators([Validators.required, Validators.min(1), Validators.max(2147483647)])
    }

    control.updateValueAndValidity()
  }

  public async search(params: PalantirSelectSearchParams): Promise<PalantirSelectResult<Company>> {
    if (params.searchTerm) this.companiesService.filterForm.get('nameContains')?.setValue(params.searchTerm)

    this.companiesService.filterForm.get('archived')?.setValue(false)
    this.companiesService.limit.set(params.take ?? 10)
    this.companiesService.offset.set(params.skip ?? 0)
    const { items, totalCount } = (await this.companiesService.searchPage()).data?.companies as CompanyPagedResult

    return {
      items,
      totalCount
    }
  }

  public mapOption(option: Company): PalantirSelectOption {
    const result: PalantirSelectOption = {
      id: option.id,
      title: option.name,
      description: '',
      icon: 'business-outline',
      avatarUrl: option.avatarUrl || '',
    }

    return result
  }
}
